import React from 'react';
import {
  Camera, Video, Eye, Scan, Zap, Shield,
  TrendingUp, Database,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const ImageVideoAnalyticsPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Image & Video Analytics',
      title: (
        <>
          AI-powered visual analytics <em>for renewable energy</em>
        </>
      ),
      lede: 'Leverage advanced image and video analytics to monitor asset health, detect defects, and optimize maintenance. Our AI-powered visual inspection platform provides comprehensive insights for renewable energy assets.',
      image: '/assets/img/general/solar-cells-4518932_1280.jpg',
      imageAlt: 'Close inspection of solar cells',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '98%', label: 'Detection accuracy' },
        { value: '50%', label: 'Faster inspections' },
        { value: 'Real-time', label: 'Analysis' },
        { value: '24/7', label: 'Monitoring' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'A complete visual analytics solution',
      lede: 'Our image and video analytics platform provides comprehensive visual inspection capabilities for renewable energy assets.',
      body: [
        'Thermal, visual and video streams are processed through the same detection pipeline, so a hotspot found by a drone and a crack found on the ground land in one inspection record against the same asset.',
      ],
      checklist: [
        'Thermal imaging and hotspot detection',
        'Crack and damage identification in solar panels',
        'Wind turbine blade inspection and analysis',
        'Automated defect classification and reporting',
        'Drone-based visual inspection integration',
        'Real-time anomaly detection and alerts',
        'Historical trend analysis and reporting',
        'Integration with maintenance management systems',
      ],
      image: '/assets/img/general/photovoltaic-4525178_1280.jpg',
      imageAlt: 'Photovoltaic panels being inspected',
      imageCaption: 'AI-powered image and video analysis for comprehensive asset monitoring',
    }}
    capabilities={{
      eyebrow: 'Analytics features',
      title: 'Advanced visual analytics',
      lede: 'Comprehensive image and video analysis capabilities.',
      items: [
        {
          icon: Camera,
          title: 'Image analysis',
          description: 'Advanced image processing and analysis for defect detection and asset condition assessment.',
        },
        {
          icon: Video,
          title: 'Video analytics',
          description: 'Real-time video analysis for continuous monitoring and anomaly detection.',
        },
        {
          icon: Scan,
          title: 'AI-powered detection',
          description: 'Machine learning algorithms for automated defect identification and classification.',
        },
        {
          icon: Eye,
          title: 'Visual inspection',
          description: 'Comprehensive visual inspection capabilities for solar panels, wind turbines, and equipment.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Analytics types',
      title: 'Comprehensive visual analysis',
      lede: 'Multiple analysis techniques for complete asset visibility.',
      items: [
        {
          icon: Scan,
          title: 'Thermal imaging',
          description: 'Detect hotspots, temperature anomalies, and electrical issues with thermal analysis.',
        },
        {
          icon: Eye,
          title: 'Visual defect detection',
          description: 'Identify cracks, soiling, shading, and physical damage using advanced image processing.',
        },
        {
          icon: Video,
          title: 'Real-time monitoring',
          description: 'Continuous video monitoring for security, safety, and operational oversight.',
        },
        {
          icon: Camera,
          title: 'Asset documentation',
          description: 'Comprehensive visual documentation for compliance, reporting, and asset management.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Key benefits',
      title: 'Transform asset management',
      lede: 'Achieve superior results with visual analytics.',
      items: [
        {
          icon: Zap,
          title: 'Early detection',
          description: 'Identify issues early before they impact performance or require costly repairs.',
        },
        {
          icon: Shield,
          title: 'Reduce downtime',
          description: 'Minimize equipment downtime with proactive maintenance based on visual insights.',
        },
        {
          icon: TrendingUp,
          title: 'Improve efficiency',
          description: 'Optimize asset performance by addressing visual defects and anomalies quickly.',
        },
        {
          icon: Database,
          title: 'Comprehensive records',
          description: 'Maintain detailed visual records of asset conditions over time.',
        },
      ],
    }}
    cta={{
      title: 'Ready for visual analytics?',
      body: 'Start leveraging AI-powered image and video analytics for your assets.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default ImageVideoAnalyticsPage;
